import React, { useState, useEffect } from 'react';
import { X, Upload, Package, DollarSign, BarChart3, Info, Trash2 } from 'lucide-react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';

const EnhancedPartModal = ({ part, vendors, onClose }) => {
  const [formData, setFormData] = useState({
    name: '', sku: '', barcode: '', brand: '', model: '', category: '', subCategory: '',
    unit: 'pcs', location: '', quantity: 0, minStock: 10, maxStock: 100,
    costPrice: 0, price: 0, b2bMargin: 0, b2cMargin: 0, wholesalePrice: 0, retailPrice: 0,
    taxRate: 0, weight: '', dimensions: '', description: '', notes: '', imageUrl: '',
    status: 'ACTIVE', isTrackable: true, expiryDate: '', vendorId: '',
    images: ['', '', '', '', '']
  });
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([null, null, null, null, null]);
  const [previewUrls, setPreviewUrls] = useState(['', '', '', '', '']);
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => {
    if (part) {
      setFormData({
        name: part.name || '', sku: part.sku || '', barcode: part.barcode || '',
        brand: part.brand || '', model: part.model || '', category: part.category || '',
        subCategory: part.subCategory || '', unit: part.unit || 'pcs', location: part.location || '',
        quantity: part.quantity || 0, minStock: part.minStock || 10, maxStock: part.maxStock || 100,
        costPrice: part.costPrice || 0, price: part.price || 0, b2bMargin: part.b2bMargin || 0,
        b2cMargin: part.b2cMargin || 0, wholesalePrice: part.wholesalePrice || 0,
        retailPrice: part.retailPrice || 0, taxRate: part.taxRate || 0, weight: part.weight || '',
        dimensions: part.dimensions || '', description: part.description || '', notes: part.notes || '',
        imageUrl: part.imageUrl || '', status: part.status || 'ACTIVE',
        isTrackable: part.isTrackable !== undefined ? part.isTrackable : true,
        expiryDate: part.expiryDate ? new Date(part.expiryDate).toISOString().split('T')[0] : '',
        vendorId: part.vendorId || '',
        images: part.images || ['', '', '', '', '']
      });
      if (part.imageUrl) setPreviewUrls(prev => [part.imageUrl, ...prev.slice(1)]);
      if (part.images) setPreviewUrls(part.images);
    }
  }, [part]);

  useEffect(() => {
    const cost = parseFloat(formData.costPrice) || 0;
    const b2bMargin = parseFloat(formData.b2bMargin) || 0;
    const b2cMargin = parseFloat(formData.b2cMargin) || 0;
    const taxRate = parseFloat(formData.taxRate) || 0;
    
    if (cost > 0) {
      const wholesalePrice = cost + (cost * b2bMargin / 100);
      const retailPrice = cost + (cost * b2cMargin / 100);
      const finalPrice = Math.max(wholesalePrice, retailPrice);
      const priceWithTax = finalPrice + (finalPrice * taxRate / 100);
      
      setFormData(prev => ({
        ...prev,
        wholesalePrice: wholesalePrice.toFixed(2),
        retailPrice: retailPrice.toFixed(2),
        price: priceWithTax.toFixed(2)
      }));
    }
  }, [formData.costPrice, formData.b2bMargin, formData.b2cMargin, formData.taxRate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let imageUrl = formData.imageUrl;
      
      const images = [...formData.images];
      
      for (let i = 0; i < selectedFiles.length; i++) {
        if (selectedFiles[i]) {
          const uploadFormData = new FormData();
          uploadFormData.append('image', selectedFiles[i]);
          
          const uploadResponse = await axios.post('/api/upload', uploadFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          images[i] = uploadResponse.data.url;
        }
      }
      
      if (selectedFiles[0]) imageUrl = images[0];
      
      const submitData = { ...formData, imageUrl, images };
      
      if (part) {
        await axios.put(`/api/parts/${part.id}`, submitData);
        toast.success('Part updated successfully');
      } else {
        await axios.post('/api/parts', submitData);
        toast.success('Part created successfully');
      }
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileSelect = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      const newSelectedFiles = [...selectedFiles];
      newSelectedFiles[index] = file;
      setSelectedFiles(newSelectedFiles);
      
      const url = URL.createObjectURL(file);
      const newPreviewUrls = [...previewUrls];
      newPreviewUrls[index] = url;
      setPreviewUrls(newPreviewUrls);
      
      const newImages = [...formData.images];
      newImages[index] = url;
      setFormData(prev => ({ ...prev, images: newImages }));
    }
  };

  const tabs = [
    { id: 'basic', name: 'Basic Info', icon: Package },
    { id: 'pricing', name: 'Pricing', icon: DollarSign },
    { id: 'inventory', name: 'Inventory', icon: BarChart3 },
    { id: 'details', name: 'Details', icon: Info }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            {part ? 'Edit Part' : 'Add New Part'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="p-6 max-h-[60vh] overflow-y-auto">
          {activeTab === 'basic' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Part Name *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} required
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SKU *</label>
                  <input type="text" name="sku" value={formData.sku} onChange={handleChange} required
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Barcode</label>
                  <input type="text" name="barcode" value={formData.barcode} onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                  <input type="text" name="brand" value={formData.brand} onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                  <input type="text" name="model" value={formData.model} onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select name="category" value={formData.category} onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500">
                    <option value="">Select Category</option>
                    <option value="Engine">Engine</option>
                    <option value="Brakes">Brakes</option>
                    <option value="Suspension">Suspension</option>
                    <option value="Electrical">Electrical</option>
                    <option value="Transmission">Transmission</option>
                    <option value="AC">AC</option>
                    <option value="Cooling">Cooling</option>
                    <option value="Exhaust">Exhaust</option>
                    <option value="Body">Body</option>
                    <option value="Interior">Interior</option>
                    <option value="Filters">Filters</option>
                    <option value="Fluids">Fluids</option>
                    <option value="Belts">Belts</option>
                    <option value="Gaskets">Gaskets</option>
                    <option value="Sensors">Sensors</option>
                    <option value="Tires">Tires</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sub Category</label>
                  <input type="text" name="subCategory" value={formData.subCategory} onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                  <select name="unit" value={formData.unit} onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500">
                    <option value="pcs">Pieces</option>
                    <option value="kg">Kilograms</option>
                    <option value="ltr">Liters</option>
                    <option value="mtr">Meters</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select name="status" value={formData.status} onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500">
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                    <option value="DISCONTINUED">Discontinued</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'pricing' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cost Price</label>
                  <input type="number" name="costPrice" value={formData.costPrice} onChange={handleChange} step="0.01"
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tax Rate (%)</label>
                  <input type="number" name="taxRate" value={formData.taxRate} onChange={handleChange} step="0.01"
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">B2B Margin (%)</label>
                  <input type="number" name="b2bMargin" value={formData.b2bMargin} onChange={handleChange} step="0.01"
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">B2C Margin (%)</label>
                  <input type="number" name="b2cMargin" value={formData.b2cMargin} onChange={handleChange} step="0.01"
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Wholesale Price</label>
                  <input type="number" name="wholesalePrice" value={formData.wholesalePrice} readOnly
                    className="w-full px-3 py-2 border rounded-md bg-gray-50" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Retail Price</label>
                  <input type="number" name="retailPrice" value={formData.retailPrice} readOnly
                    className="w-full px-3 py-2 border rounded-md bg-gray-50" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Final Selling Price (with Tax)</label>
                <input type="number" name="price" value={formData.price} readOnly
                  className="w-full px-3 py-2 border rounded-md bg-gray-100 font-semibold" />
              </div>
            </div>
          )}

          {activeTab === 'inventory' && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Quantity</label>
                  <input type="number" name="quantity" value={formData.quantity} onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Stock</label>
                  <input type="number" name="minStock" value={formData.minStock} onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Stock</label>
                  <input type="number" name="maxStock" value={formData.maxStock} onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input type="text" name="location" value={formData.location} onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
                  <select name="vendorId" value={formData.vendorId} onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500">
                    <option value="">Select Vendor</option>
                    {vendors.map(vendor => (
                      <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input type="checkbox" name="isTrackable" checked={formData.isTrackable} onChange={handleChange}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  <span className="ml-2 text-sm text-gray-700">Track inventory levels</span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                <input type="date" name="expiryDate" value={formData.expiryDate} onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
          )}

          {activeTab === 'details' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Weight (kg)</label>
                  <input type="number" name="weight" value={formData.weight} onChange={handleChange} step="0.01"
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dimensions (L×W×H)</label>
                  <input type="text" name="dimensions" value={formData.dimensions} onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea name="description" value={formData.description} onChange={handleChange} rows="3"
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea name="notes" value={formData.notes} onChange={handleChange} rows="2"
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Images (up to 5)</label>
                <div className="grid grid-cols-5 gap-4">
                  {[0, 1, 2, 3, 4].map((index) => (
                    <div key={index} className="space-y-2 relative">
                      <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                        {previewUrls[index] ? (
                          <img src={previewUrls[index]} alt={`Preview ${index + 1}`} className="w-full h-full object-cover rounded-lg" />
                        ) : (
                          <div className="flex flex-col items-center">
                            <Upload className="w-6 h-6 text-gray-400" />
                            <span className="text-xs text-gray-500">Image {index + 1}</span>
                          </div>
                        )}
                        <input type="file" accept="image/jpeg,image/png,image/jpg" onChange={(e) => handleFileSelect(e, index)} className="hidden" />
                      </label>
                      {previewUrls[index] && (
                        <button
                          type="button"
                          onClick={() => {
                            const newPreviewUrls = [...previewUrls];
                            newPreviewUrls[index] = '';
                            setPreviewUrls(newPreviewUrls);
                            const newSelectedFiles = [...selectedFiles];
                            newSelectedFiles[index] = null;
                            setSelectedFiles(newSelectedFiles);
                            const newImages = [...formData.images];
                            newImages[index] = '';
                            setFormData(prev => ({ ...prev, images: newImages }));
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
            <button type="button" onClick={onClose}
              className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
              {loading ? 'Saving...' : (part ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EnhancedPartModal;